import { useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

type ClientType = components["schemas"]["ClientType"];

const CLIENT_TYPES: ClientType[] = ["Live", "Sandbox", "Demo"];
const DEFAULT_CLIENT_TYPE: ClientType = "Live";
const DEFAULT_DOLFIN_REV_SHARE = "0.30";
const DEFAULT_CLIENT_REV_SHARE = "0.20";

interface CreateClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

export function CreateClientDialog({ open, onOpenChange, onCreated }: CreateClientDialogProps) {
  const [name, setName] = useState("");
  const [type, setType] = useState<ClientType>(DEFAULT_CLIENT_TYPE);
  const [dolfinRevenueSharePercent, setDolfinRevenueSharePercent] = useState(DEFAULT_DOLFIN_REV_SHARE);
  const [paymentRevenueSharePercent, setPaymentRevenueSharePercent] = useState(DEFAULT_CLIENT_REV_SHARE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function resetForm() {
    setName("");
    setType(DEFAULT_CLIENT_TYPE);
    setDolfinRevenueSharePercent(DEFAULT_DOLFIN_REV_SHARE);
    setPaymentRevenueSharePercent(DEFAULT_CLIENT_REV_SHARE);
    setSubmitting(false);
    setError(null);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError("Client name is required.");
      return;
    }

    const dolfin = parseFloat(dolfinRevenueSharePercent);
    const client = parseFloat(paymentRevenueSharePercent);
    if (Number.isNaN(dolfin) || dolfin < 0) {
      setError("Dolfin revenue share must be a non-negative number.");
      return;
    }
    if (Number.isNaN(client) || client < 0) {
      setError("Client revenue share must be a non-negative number.");
      return;
    }

    setSubmitting(true);
    const { error: apiError } = await api.POST("/clients", {
      body: {
        name: name.trim(),
        type,
        dolfinRevenueSharePercent: dolfin,
        paymentRevenueSharePercent: client,
      },
    });

    if (apiError) {
      setError("Failed to create client.");
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    handleOpenChange(false);
    onCreated();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create Client</DialogTitle>
            <DialogDescription>
              Register a new client. Revenue share rates apply to the application_fee_amount on every
              card payment made by this client's organisations.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="client-name">Name</Label>
              <Input
                id="client-name"
                placeholder="Client name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <div className="flex flex-wrap gap-2">
                {CLIENT_TYPES.map((option) => {
                  const selected = type === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setType(option)}
                      aria-pressed={selected}
                      className={cn(
                        "rounded-full border px-3 py-1 text-sm transition-colors",
                        selected
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-input bg-background hover:bg-accent hover:text-accent-foreground",
                      )}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client-dolfin-rev">Dolfin revenue share (%)</Label>
                <Input
                  id="client-dolfin-rev"
                  type="number"
                  step="0.01"
                  min="0"
                  value={dolfinRevenueSharePercent}
                  onChange={(e) => setDolfinRevenueSharePercent(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client-client-rev">Client revenue share (%)</Label>
                <Input
                  id="client-client-rev"
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentRevenueSharePercent}
                  onChange={(e) => setPaymentRevenueSharePercent(e.target.value)}
                />
              </div>
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
